import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';

const AdminInstallerUpdateView = () => {
  const [installerFile, setInstallerFile] = useState(null);
  const [version, setVersion] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [productType, setProductType] = useState('revit'); // Default to BAMROC Revit

  const handleFileChange = (event) => {
    setInstallerFile(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!installerFile || !version || !releaseNotes) {
      setErrorMessage('All fields are required.');
      return;
    }

    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const jwt = localStorage.getItem('jwttoken');
      const formData = new FormData();
      formData.append('installerFile', installerFile);
      formData.append('version', version);
      formData.append('releaseNotes', releaseNotes);

      // Dynamically select API endpoint
      const endpoint =
        productType === 'revit'
          ? '/api/admin/adminupdates/upload-installer'
          : '/api/admin/adminupdates/upload-installer-navis';

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to upload installer and update manifest.');
      }

      const result = await response.json();
      if (result.success) {
        setSuccessMessage('Installer uploaded and manifest updated successfully.');
        setInstallerFile(null);
        setVersion('');
        setReleaseNotes('');
      } else {
        setErrorMessage(result.message || 'Something went wrong.');
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" sx={{ marginBottom: 3, fontWeight: 'bold' }}>
        Upload New Installer and Update Manifest
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ marginBottom: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Paper sx={{ padding: 3, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          {/* Product Selection */}
          <FormControl component="fieldset" sx={{ marginBottom: 2 }}>
            <FormLabel component="legend">Select Product</FormLabel>
            <RadioGroup
              row
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
            >
              <FormControlLabel
                value="revit"
                control={<Radio />}
                label="BAMROC Revit"
              />
              <FormControlLabel
                value="navisworks"
                control={<Radio />}
                label="BAMROC Navisworks"
              />
            </RadioGroup>
          </FormControl>

          <TextField
            label="Version"
            fullWidth
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            sx={{ marginBottom: 2 }}
            required
          />
          <TextField
            label="Release Notes"
            fullWidth
            multiline
            rows={4}
            value={releaseNotes}
            onChange={(e) => setReleaseNotes(e.target.value)}
            sx={{ marginBottom: 2 }}
            required
          />
          <Button
            variant="contained"
            component="label"
            sx={{
              marginBottom: 2,
              display: 'block',
              backgroundColor: '#CC9A39',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#B8860B' },
            }}
          >
            Select Installer File
            <input
              type="file"
              hidden
              accept=".exe,.zip,.msi"
              onChange={handleFileChange}
            />
          </Button>
          {installerFile && (
            <Typography variant="body1" sx={{ marginBottom: 2 }}>
              Selected File: {installerFile.name}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{
              backgroundColor: '#CC9A39',
              '&:hover': { backgroundColor: '#B8860B' },
            }}
          >
            {loading ? <CircularProgress size={20} /> : 'Upload Installer'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default AdminInstallerUpdateView;
